import { useCallback, useMemo, useRef, useState } from 'react';
import { Typography } from 'antd';

import { CustomNotification } from 'common/components';

import { useCallbackRef, useGetMemberId } from 'hooks';
import { useSaveWizardForm } from './useSaveWizardForm';
import { useProductValidation } from './useValidateWizardForm';
import { useGetWizardFormItems } from './useGetWizardFormItems';

import {
  createEmptyWizardProduct,
  mapWizardFormItemsToProducts,
  applyHierarchyToProduct,
  deriveAnswersFromResponse,
  ANSWER_KEYS,
  sanitizeProduct,
  autoPopulateCaseGtin,
  calculateDisplayShipperTotals,
  calculateMultipackTotals,
} from '../utils/wizardProduct';

import {
  applyValidationResult,
  replaceUndefinedWithNull,
  validateWizardProduct,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/validation';

import {
  confirmDialog,
  revertCell,
  refreshCells,
  getNoCodeCaseSideEffects,
  syncShipperItems,
  checkErrorType,
  checkWarningType,
  computeUpdatedErrors,
  resolveCaseGtinAutoFill,
} from '../utils/wizardGrid';

import { DISPLAY_SHIPPER_DISABLED_FIELDS } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/constants/wizardOptions';

const withoutUpcs = (items, upcs) =>
  (items ?? []).filter((item) => !upcs.has(item.itemUpc));

const removeItemsWithUpcs = (product, upcs) => {
  const displayShipperItems = withoutUpcs(product.displayShipperItems, upcs);
  const multipackItems = withoutUpcs(product.multipackItems, upcs);

  const isShipperChanged =
    displayShipperItems.length !== (product.displayShipperItems?.length ?? 0);
  const isMultipackChanged =
    multipackItems.length !== (product.multipackItems?.length ?? 0);

  if (!isShipperChanged && !isMultipackChanged) return product;

  return {
    ...product,
    ...(isShipperChanged && {
      displayShipperItems,
      displayShippersTotalUnit:
        calculateDisplayShipperTotals(displayShipperItems) || null,
    }),
    ...(isMultipackChanged && {
      multipackItems,
      multipackTotalUnit: calculateMultipackTotals(multipackItems) || null,
    }),
  };
};

export const useOnlineGrid = (state, actions) => {
  const gridRef = useRef();
  const { memberId } = useGetMemberId();

  const { validateWizardForm, isValidating } = useProductValidation(
    state,
    actions
  );
  const { saveWizardForm, saveWizardFormAsync, isSaving } = useSaveWizardForm(
    state,
    actions
  );
  const { getWizardFormItems } = useGetWizardFormItems();

  const [productModal, setProductModal] = useState({
    visible: false,
    mode: null,
    product: null,
    validationErrors: null,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [recaptureModalVisible, setRecaptureModalVisible] = useState(false);
  const [recaptureTargetRow, setRecaptureTargetRow] = useState(null);

  /**
   * Enriched UPC option objects derived directly from state.products.
   * Shape: `{ upc: string, label: string }` where label is "UPC — Product Name".
   *
   * Computed here instead of being passed as a parameter so every consumer
   * (handleAddNewProduct, WizardGrid dropdown) reads from the same source
   * without any prop-threading.
   */
  const unitUpcOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    for (const product of state.products) {
      const upc = product.unitUpc;
      if (!upc || seen.has(upc)) continue;
      seen.add(upc);
      const name = product.productName || product.brandName || '';
      options.push({
        upc,
        label: name ? `${upc} — ${name}` : upc,
      });
    }
    return options;
  }, [state.products]);

  const onSelectionChange = useCallback(() => {
    const selectedRows = gridRef.current?.api?.getSelectedRows();
    setSelectedRow(selectedRows?.[0] ?? null);
  }, []);

  // ── New Product: add row with auto-mapped UPC where possible ────────────
  /**
   * Creates a new empty product and pre-fills `unitUpc` when exactly one UPC
   * from Core Data is not yet claimed by an existing product row.
   *
   * unitUpcOptions is derived from state internally — no parameter needed.
   */
  const handleAddNewProduct = useCallback(async () => {
    const claimedUpcs = new Set(
      state.products.map((p) => p.unitUpc).filter(Boolean)
    );

    const unclaimedOptions = unitUpcOptions.filter(
      ({ upc }) => !claimedUpcs.has(upc)
    );

    // Auto-map only when there is exactly one unclaimed UPC — unambiguous
    const autoUpc =
      unclaimedOptions.length === 1 ? unclaimedOptions[0].upc : '';

    const newProduct = {
      ...createEmptyWizardProduct(state?.answers),
      unitUpc: autoUpc,
    };

    // Validate immediately so the status icon reflects the pre-filled state
    const validatedProduct = await validateWizardProduct(newProduct);
    actions.addProduct(validatedProduct);

    // Wait one tick for AG Grid to render the new row
    setTimeout(() => {
      const api = gridRef.current?.api;
      if (!api) return;

      const rowNode = api.getRowNode(validatedProduct.uniqueId);
      if (rowNode) {
        api.ensureNodeVisible(rowNode, 'bottom');
        rowNode.setSelected(true);
      }
      api.startEditingCell({
        rowKey: validatedProduct.uniqueId,
        colKey: 'productName',
      });
    }, 0);
  }, [actions, state?.answers, state.products, unitUpcOptions]);

  // ── Recapture: open product search modal (toolbar button) ──────────────
  const handleAddRecapture = useCallback(() => {
    setRecaptureTargetRow(null);
    setRecaptureModalVisible(true);
  }, []);

  // ── Recapture: open modal targeting a specific row ──────────────────────
  const handleRecaptureRow = useCallback((rowData) => {
    setRecaptureTargetRow(rowData);
    setRecaptureModalVisible(true);
  }, []);

  const handleCloseRecaptureModal = useCallback(() => {
    setRecaptureTargetRow(null);
    setRecaptureModalVisible(false);
  }, []);

  // ── Recapture confirm: fetch wizardFormItems, map, append ───────────────
  /**
   * Called by RecaptureModal when the user confirms their selection.
   *
   * `selectedProducts` is an array of catalog product objects, each carrying
   * at minimum a `productItemId` (or `productUnitId`) field.
   *
   * Flow:
   * 1. Extract `productItemId` values from the selection.
   * 2. Fetch `wizard/get-wizard-form-items` via `queryClient.fetchQuery`
   *    (5-minute stale time — repeated opens of the same selection are free).
   * 3. Map the response through `mapWizardFormItemsToProducts` to get
   *    properly shaped wizard products with `hierarchies` attached.
   * 4. Append every new product to wizard state (never replace existing rows).
   * 5. Redraw AG Grid rows so cell renderers reflect the updated state.
   *
   * @param {Array<{ productItemId?: number, productUnitId?: number }>} selectedProducts
   */
  const handleAddRecaptureProducts = useCallbackRef(
    async (selectedProducts) => {
      if (!selectedProducts?.length) {
        setRecaptureTargetRow(null);
        setRecaptureModalVisible(false);
        return;
      }

      const productIds = selectedProducts.map(
        (p) => p.productItemId ?? p.productUnitId ?? p.id
      );

      let newProducts;

      try {
        const response = await getWizardFormItems({ productIds });

        // Adding products may turn answers on, never off — otherwise tabs and
        // columns the user asked for (e.g. Beer/Wine/Spirits) would vanish.
        const derivedAnswers = deriveAnswersFromResponse(response?.data);
        const existingAnswers = state.answers ?? {};
        const mergedAnswers = Object.fromEntries(
          ANSWER_KEYS.map((key) => [
            key,
            Boolean(existingAnswers[key] || derivedAnswers[key]),
          ])
        );
        actions.mergeAnswers(mergedAnswers);

        const wizardFormItems = response?.data?.wizardFormItems ?? [];
        const mappedProducts = mapWizardFormItemsToProducts(wizardFormItems);

        const hydratedProducts = mappedProducts.map((product) => {
          if (!product.hierarchyId) return sanitizeProduct(product);
          const matchedHierarchy = product.hierarchies?.find(
            (h) => h.productHierarchyId === product.hierarchyId
          );
          const withHierarchy = matchedHierarchy
            ? applyHierarchyToProduct(product, matchedHierarchy)
            : product;
          return sanitizeProduct(withHierarchy);
        });

        // ── API is the sole source of validation for recaptured products ──
        // Skip validateWizardProduct to avoid stale client errors surviving.
        const apiResponse = await validateWizardForm({
          products: hydratedProducts,
          memberId,
          showMessage: false,
        });

        newProducts = applyValidationResult({
          response: apiResponse,
          products: hydratedProducts,
        }).map((p) => sanitizeProduct(p));
      } catch {
        CustomNotification.error(
          'Failed to load product data. Please try again.'
        );
        return;
      }

      if (!newProducts.length) {
        setRecaptureTargetRow(null);
        setRecaptureModalVisible(false);
        return;
      }

      if (recaptureTargetRow) {
        const [replacement] = newProducts;
        actions.setProducts(
          state.products.map((p) =>
            p.uniqueId === recaptureTargetRow.uniqueId
              ? { ...replacement, uniqueId: recaptureTargetRow.uniqueId }
              : p
          )
        );
      } else {
        actions.setProducts([...state.products, ...newProducts]);
      }

      setRecaptureTargetRow(null);
      setRecaptureModalVisible(false);

      setTimeout(() => {
        gridRef.current?.api?.redrawRows();
      }, 0);
    }
  );

  const handleClearSelectedRow = useCallback(() => {
    setSelectedRow(null);
    gridRef.current?.api?.deselectAll();
  }, []);

  const handleCellValueChanged = useCallbackRef(async (params) => {
    const {
      colDef: { field },
      newValue,
      data: { uniqueId },
    } = params;

    // ── Guard: displayShipper → No ─────────────────────────────────────────────
    if (field === 'displayShipper' && newValue === false) {
      const current = state.products.find((p) => p.uniqueId === uniqueId);
      if ((current?.displayShipperItems ?? []).length > 0) {
        const confirmed = await confirmDialog(
          <Typography.Text>
            This product is currently listed in the Shipper Contents tab with
            shipper items configured. Changing Display/Shipper to{' '}
            <Typography.Text strong>No</Typography.Text> will remove it from the
            Shipper tab and permanently clear all entered shipper items. Are you
            sure you want to continue?
          </Typography.Text>
        );
        if (!confirmed) {
          revertCell(gridRef, uniqueId, 'displayShipper', true);
          return;
        }
      }
    }

    // ── Build liveProduct ──────────────────────────────────────────────────────
    const preProduct = {
      ...params.node.data,
      ...(state.products.find((p) => p.uniqueId === uniqueId) ?? {}),
      [field]: newValue,
    };

    // Strip errors on fields that sanitizeProduct will clear when displayShipper → true
    const preErrors =
      field === 'displayShipper' && newValue === true
        ? (preProduct.validationErrors ?? []).filter(
            (e) => !DISPLAY_SHIPPER_DISABLED_FIELDS.has(e?.field)
          )
        : (preProduct.validationErrors ?? []);

    const liveProduct = {
      ...preProduct,
      validationErrors: preErrors.length > 0 ? preErrors : null,
    };

    // ── Sync caseGtin autofill (before any await!) ──────────────────────────────
    // AG Grid's Tab-to-next-cell navigation mounts the caseGtin editor
    // synchronously, immediately after this commit — before any awaited
    // validation below resolves. That editor reads its initial value once on
    // mount, so filling caseGtin only after the await would be too late; it
    // must land in the grid's row data before control returns to AG Grid.
    if (field === 'caseUpc12') {
      const { caseGtin: autoGtin } = autoPopulateCaseGtin(liveProduct);
      if (autoGtin && autoGtin !== liveProduct.caseGtin) {
        revertCell(gridRef, uniqueId, 'caseGtin', autoGtin);
      }
    }

    // ── Side effects + revalidate ──────────────────────────────────────────────
    const sideEffects =
      field === 'noCodeCase'
        ? getNoCodeCaseSideEffects(newValue, liveProduct)
        : {};

    const revalidated = await validateWizardProduct({
      ...liveProduct,
      ...sideEffects,
    });

    // ── Merge errors ───────────────────────────────────────────────────────────
    const updatedErrors = computeUpdatedErrors({
      field,
      newValue,
      liveProduct,
      revalidated,
    });

    // ── Auto-populate caseGtin from a valid caseUpc12 (only when caseGtin is empty) ──
    const caseGtinAutoFill = resolveCaseGtinAutoFill({
      field,
      newValue,
      liveProduct,
      revalidated,
    });

    // ── Sync description fields when productDescription changes ─────────────────
    const descriptionSync =
      field === 'productDescription'
        ? {
            displayShippersDescription: newValue,
            multipackDescription: newValue,
          }
        : {};

    // ── Build updated product ──────────────────────────────────────────────────
    const updatedProduct = sanitizeProduct({
      ...liveProduct,
      ...sideEffects,
      ...caseGtinAutoFill,
      ...descriptionSync,
      validationErrors: updatedErrors.length > 0 ? updatedErrors : null,
      status: updatedErrors.some((e) => checkErrorType(e.messageType))
        ? 'error'
        : updatedErrors.some((e) => checkWarningType(e.messageType))
          ? 'warning'
          : 'success',
      canSubmit: !updatedErrors.some((e) => checkErrorType(e.messageType)),
    });

    // ── Commit to state ────────────────────────────────────────────────────────
    const mergedProducts = state.products.map((p) =>
      p.uniqueId === uniqueId ? updatedProduct : p
    );
    actions.setProducts(
      syncShipperItems(mergedProducts, uniqueId, updatedProduct, params)
    );

    // ── Refresh cells ──────────────────────────────────────────────────────────
    refreshCells(gridRef, uniqueId, [field, 'status']);

    if (field === 'noCodeCase')
      setTimeout(
        () => refreshCells(gridRef, uniqueId, ['caseGtin', 'caseUpc12']),
        50
      );

    if (field === 'caseUpc12' && caseGtinAutoFill.caseGtin)
      revertCell(gridRef, uniqueId, 'caseGtin', updatedProduct.caseGtin);

    if (field === 'displayShipper')
      refreshCells(gridRef, uniqueId, [
        'unitUpc',
        'merchandisedInTray',
        'caseGtin',
        'caseUpc12',
        'noCodeCase',
        'innerPackUpc',
        'innerPackGtin',
        'innerPackPackSize',
        'innerPackSellable',
      ]);

    if (field === 'variableWeight')
      refreshCells(gridRef, uniqueId, [
        'unitUpc', 'noCodeCase', 'caseGtin', 'caseUpc12',
        'innerPackUpc', 'innerPackGtin', 'innerPackPackSize',
        'innerPackSellable', 'caseInnerPackCount',
      ]);

    if (field === 'foodServiceBulkItems')
      refreshCells(gridRef, uniqueId, [
        'innerPackUpc',
        'innerPackGtin',
        'innerPackPackSize',
        'caseSellable',
      ]);

    if (field === 'innerPackSellable')
      refreshCells(gridRef, uniqueId, ['innerPackUpc', 'innerPackGtin']);
  });

  const handleEditProduct = useCallback(() => {
    if (!selectedRow) {
      CustomNotification.warning('Please select a product to edit');
      return;
    }

    // Always read latest data from state, not from stale selectedRow snapshot
    const latestProduct =
      state.products.find((p) => p.uniqueId === selectedRow.uniqueId) ??
      selectedRow;

    setProductModal({
      visible: true,
      mode: 'edit',
      product: latestProduct,
      validationErrors: [],
    });
  }, [selectedRow, state.products]);

  const handleDeleteProduct = useCallback(() => {
    if (!selectedRow) {
      CustomNotification.warning('Please select a product to remove');
      return;
    }

    const deletedProduct =
      state.products.find((p) => p.uniqueId === selectedRow.uniqueId) ??
      selectedRow;
    const remainingProducts = state.products.filter(
      (p) => p.uniqueId !== deletedProduct.uniqueId
    );

    const upcsStillInGrid = new Set(
      remainingProducts.flatMap((p) => [p.unitUpc, p.caseGtin])
    );
    const deletedUpcs = new Set(
      [deletedProduct.unitUpc, deletedProduct.caseGtin].filter(
        (upc) => upc && !upcsStillInGrid.has(upc)
      )
    );

    actions.setProducts(
      remainingProducts.map((p) => removeItemsWithUpcs(p, deletedUpcs))
    );
    setSelectedRow(null);
  }, [selectedRow, actions, state.products]);

  const handleSubmitProduct = useCallback(
    async (values) => {
      if (productModal.mode === 'add') {
        const newProduct = {
          ...createEmptyWizardProduct(),
          ...replaceUndefinedWithNull(values),
        };
        actions.addProduct(newProduct);
      } else if (productModal.mode === 'edit') {
        const changedProduct = {
          ...selectedRow,
          ...values,
        };

        const response = await validateWizardForm({
          products: [changedProduct],
          memberId,
        });

        const [validatedProduct] = applyValidationResult({
          response,
          products: [changedProduct],
        });

        actions.setProducts(
          state.products.map((product) =>
            product.uniqueId === selectedRow.uniqueId
              ? validatedProduct
              : product
          )
        );
      }

      setProductModal({
        visible: false,
        mode: null,
        product: null,
        validationErrors: null,
      });
      setSelectedRow(null);
      gridRef.current?.api?.deselectAll();
    },
    [
      productModal.mode,
      selectedRow,
      actions,
      state.products,
      validateWizardForm,
      memberId,
    ]
  );

  const handleCloseProductModal = useCallback(() => {
    setProductModal({
      visible: false,
      mode: null,
      product: null,
      validationErrors: null,
    });
  }, []);

  const handleValidate = useCallback(
    ({ products = state.products, memberId, showMessage = true }) => {
      return validateWizardForm({
        products,
        memberId,
        showMessage,
      });
    },
    [validateWizardForm, state.products]
  );

  const handleSave = useCallback(() => {
    saveWizardForm({ state, memberId });
  }, [memberId, saveWizardForm, state]);

  return {
    gridRef,
    selectedRow,
    productModal,
    recaptureModalVisible,
    recaptureTargetRow,
    isValidating,
    isSaving,
    memberId,
    unitUpcOptions,
    onSelectionChange,
    handleClearSelectedRow,
    handleAddNewProduct,
    handleAddRecapture,
    handleRecaptureRow,
    handleAddRecaptureProducts,
    handleCloseRecaptureModal,
    handleCellValueChanged,
    handleEditProduct,
    handleDeleteProduct,
    handleSubmitProduct,
    handleCloseProductModal,
    handleValidate,
    handleSave,
    saveWizardFormAsync,
    validateWizardForm,
  };
};

export default useOnlineGrid;
